/**
 * Enrollment 必須化 gate (#734).
 *
 * `WEBAUTHN_ENROLLMENT_MODE` が `required_*` の場合、credentials 0 件のユーザーは
 * **アプリの全 page 利用を block** する。Login.tsx の modal 表示だけでは URL 直打ちで
 * bypass されてしまうため、RequireAuth 層で確実に gate する。
 *
 * 戻り値:
 *   - `requiresEnrollment: true`: enrollment 必須 + credentials 0 → blocker UI 表示
 *   - `loading: true`: config / credentials fetch 中
 *
 * 設計: useSyncExternalStore で session-wide cache を共有 (= component 間で同じ判定)。
 * 登録完了後は `markEnrolled()` で cache を invalidate して全 RequireAuth が再評価。
 */
import { useEffect, useSyncExternalStore } from 'react'
import { onAuthStateChanged } from 'firebase/auth'
import { useStore } from '../store/useStore'
import { api } from '../services/api'
import { auth } from '../lib/firebase'
import { hasTotpFactor } from '../lib/totpMfa'

// ============================================================
// Session-wide cache + pub/sub
// ============================================================

type GateState = {
  requires: boolean | null  // null = not evaluated yet
  loading: boolean
}

let state: GateState = { requires: null, loading: false }
const listeners = new Set<() => void>()

function subscribe(listener: () => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

function getSnapshot(): GateState {
  return state
}

function setState(next: Partial<GateState>): void {
  // 変更がない場合は no-op (再描画 churn を避ける)
  if (
    (next.requires === undefined || next.requires === state.requires) &&
    (next.loading === undefined || next.loading === state.loading)
  ) {
    return
  }
  // useSyncExternalStore は getSnapshot を Object.is 比較し、同一参照だと
  // state 変更でも再描画しない。可変シングルトンを in-place 変更すると
  // markEnrolled / resetEnrollmentGate の通知で gate が再評価されず
  // (リロードまで反映されない) 不具合になる。変更時は必ず新 object を割当てる。
  state = {
    requires: next.requires !== undefined ? next.requires : state.requires,
    loading: next.loading !== undefined ? next.loading : state.loading,
  }
  for (const l of listeners) l()
}

let fetchInFlight = false

async function evaluate(): Promise<void> {
  if (fetchInFlight) return
  fetchInFlight = true
  setState({ loading: true })
  try {
    const [config, credsResp] = await Promise.all([
      api.getWebauthnConfig().catch(() => null),
      api.listWebauthnCredentials().catch(() => ({ credentials: [] })),
    ])
    if (!config || config.enrollmentMode === 'disabled') {
      setState({ requires: false, loading: false })
      return
    }
    const hasCreds = (credsResp?.credentials || []).length > 0
    let requires =
      (config.enrollmentMode === 'required_all' && !hasCreds) ||
      (config.enrollmentMode === 'required_new_users' && !hasCreds)
    // #815/#838: TOTP fallback flag ON のとき、**TOTP 第2要素登録済なら
    // passkey 有無に関わらず enrollment gate を充足**とみなす(= 登録時の
    // 強要素は「passkey OR TOTP」)。
    //
    // #869 ③ race fix: evaluate() がページリロード直後に発火すると、
    // Firebase Auth state が IndexedDB から復元される前で `auth.currentUser`
    // が null のことがある → `hasTotpFactor(null)=false` で誤って
    // `requires=true` を cache → TOTP enrolled user に WebAuthn modal が出る
    // 不具合(#869 ③)。`auth.authStateReady()` を await して復元完了後に
    // 判定する。さらに `useEnrollmentGate` 側で `onAuthStateChanged` に
    // subscribe し、user 切替で再 evaluate するセーフティネットを併設。
    if (requires && config.totpFallbackEnabled) {
      try {
        await auth.authStateReady()
      } catch {
        /* fall through to hasTotpFactor check (currentUser はそのまま) */
      }
      await auth.currentUser?.reload()
      if (hasTotpFactor(auth.currentUser)) requires = false
    }
    setState({ requires, loading: false })
  } catch {
    // fail-safe: gate 通す (= login flow を詰まらせない)
    setState({ requires: false, loading: false })
  } finally {
    fetchInFlight = false
  }
}

// ============================================================
// Public API
// ============================================================

/** Passkey 登録成功後に呼ぶ — cache を invalidate して全 RequireAuth が再評価. */
export function markEnrolled(): void {
  setState({ requires: false })
}

/** #815: WebAuthn 不可層が TOTP enroll 完了時に呼ぶ (markEnrolled と同じ楽観セット). */
export function markTotpEnrolled(): void {
  setState({ requires: false })
}

/** Logout 時に呼ぶ — cache をクリア (= 別ユーザーで再ログイン時に再 fetch). */
export function resetEnrollmentGate(): void {
  setState({ requires: null, loading: false })
}

export function useEnrollmentGate(): { requiresEnrollment: boolean; loading: boolean } {
  const isLoggedIn = useStore((s) => s.isLoggedIn)
  const snapshot = useSyncExternalStore(subscribe, getSnapshot, getSnapshot)

  useEffect(() => {
    if (!isLoggedIn) {
      // logout → reset
      if (snapshot.requires !== null) {
        setState({ requires: null })
      }
      return
    }
    // 未評価かつ login 済なら fetch
    if (snapshot.requires === null && !snapshot.loading) {
      void evaluate()
    }
  }, [isLoggedIn, snapshot.requires, snapshot.loading])

  // #869 ③: ページリロード直後など、useEnrollmentGate の最初の evaluate()
  // が Firebase Auth state 復元前に走ると `hasTotpFactor` が誤って false
  // を返し、TOTP enrolled user に対しても `requires=true` で stuck する。
  // `onAuthStateChanged` の event(復元完了 or user 切替)で再 evaluate して
  // 真の TOTP factor 有無で判定し直す。flag OFF / TOTP 未 enroll の user
  // は evaluate 内で結果が同じになり再描画 churn は無い。
  useEffect(() => {
    if (!isLoggedIn) return
    const unsubscribe = onAuthStateChanged(auth, () => {
      // TOTP factor が見えるようになった可能性 → 再評価。
      // 既に確定済 false (= 通過) なら trigger しても結果同じ。
      setState({ requires: null })
      void evaluate()
    })
    return unsubscribe
  }, [isLoggedIn])

  return {
    requiresEnrollment: snapshot.requires === true,
    loading: snapshot.loading,
  }
}
